/**
 * KanbanColumn — droppable bucket of cards.
 *
 * Uses @dnd-kit's `useDroppable` so cards from any column can land here.
 * Render-prop pattern (`renderCard`) keeps card layout decoupled from the
 * column shell so intakes and projects can each render their own card markup
 * without forking this component.
 *
 * Accessibility: header gets a stable id and the card list references it via
 * `aria-labelledby`, satisfying WCAG 1.3.1 (Info and Relationships).
 */
import { useDroppable } from "@dnd-kit/core";
import { Badge, Card, Empty, Typography } from "antd";
import type { ReactNode } from "react";

interface KanbanColumnProps<T extends { id: string }> {
  id: string;
  label: string;
  items: T[];
  renderCard: (item: T) => ReactNode;
}

export function KanbanColumn<T extends { id: string }>({
  id,
  label,
  items,
  renderCard,
}: KanbanColumnProps<T>) {
  const { setNodeRef, isOver } = useDroppable({ id });
  const headerId = `board-col-${id}`;

  return (
    <Card
      size="small"
      style={{
        width: 280,
        flex: "0 0 280px",
        background: isOver ? "#EEF7FF" : "#fafafa",
        // 1.5px outline on drop-target instead of an ant border swap so the
        // column doesn't shift when toggled.
        outline: isOver ? "1.5px solid #1E79C8" : "1.5px solid transparent",
        outlineOffset: -1,
      }}
      styles={{
        body: { display: "flex", flexDirection: "column", gap: 8, minHeight: 200 },
      }}
      title={
        <div id={headerId} style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Typography.Text strong>{label}</Typography.Text>
          <Badge
            count={items.length}
            showZero
            color="#092940"
            aria-label={`${items.length} items in ${label}`}
          />
        </div>
      }
    >
      <div
        ref={setNodeRef}
        role="list"
        aria-labelledby={headerId}
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 8,
          minHeight: 160,
        }}
      >
        {items.length === 0 ? (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={<span style={{ color: "#888" }}>Drop here</span>}
            style={{ marginBlock: 24 }}
          />
        ) : (
          items.map((item) => (
            <div role="listitem" key={item.id}>
              {renderCard(item)}
            </div>
          ))
        )}
      </div>
    </Card>
  );
}
